/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as S from './landing.styles';
import assessDark from '../../../assets/PNG/assess_dark.png';
import assessLight from '../../../assets/PNG/assess_light.png';
import traditionalFlag from '../../../assets/PNG/traditionalFlag.png';
import imgUrbanDark from '../../../assets/PNG/urbanCompass_dark.png';
import imgUrbanLight from '../../../assets/PNG/urbanCompass_light.png';
import { getCurrentYear } from '../../../helper/validationHelper';
import { sendContactUsMessage } from '../../../services/publicChatBot';
// import { AppleStore, GoogleStore } from '../../../components/icons';

const LandingPage = ({ isDark, toggleTheme }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.phoneNumber ||
      !formData.message
    ) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const payload = {
        appType: 'CB',
        firstName: formData.firstName,
        lastName: formData.lastName,
        message: formData.message,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
      };
      setIsSubmitting(true);

      await sendContactUsMessage(payload);
      alert('Message sent successfully!');

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        message: '',
      });
    } catch (error) {
      setIsSubmitting(false);
      alert('Error sending message. Please try again.');
      console.error('Error:', error);
    }
  };

  const handleScrollToDemo = (e) => {
    e.preventDefault();
    const demoSection = document.getElementById('demo');
    if (demoSection) {
      demoSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  return (
    <S.PageWrapper>
      {/* Navbar */}
      <S.Navbar>
        <S.NavContent>
          <div>
            <Link to="/">
              {isDark ? (
                <S.Logo src={imgUrbanDark} alt="logo" />
              ) : (
                <S.Logo src={imgUrbanLight} alt="logo" />
              )}
            </Link>
          </div>
          <S.NavContent>
            <S.ThemeToggleButton onClick={toggleTheme}>
              <S.LightModeIcon
                $isDark={isDark}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 -960 960 960"
              >
                <path d="M480-360q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35Zm0 80q-83 0-141.5-58.5T280-480q0-83 58.5-141.5T480-680q83 0 141.5 58.5T680-480q0 83-58.5 141.5T480-280ZM200-440H40v-80h160v80Zm720 0H760v-80h160v80ZM440-760v-160h80v160h-80Zm0 720v-160h80v160h-80ZM256-650l-101-97 57-59 96 100-52 56Zm492 496-97-101 53-55 101 97-57 59Zm-98-550 97-101 59 57-100 96-56-52ZM154-212l101-97 55 53-97 101-59-57Zm326-268Z" />
              </S.LightModeIcon>
              <S.DarkModeIcon
                $isDark={isDark}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 -960 960 960"
              >
                <path d="M480-120q-150 0-255-105T120-480q0-150 105-255t255-105q14 0 27.5 1t26.5 3q-41 29-65.5 75.5T444-660q0 90 63 153t153 63q55 0 101-24.5t75-65.5q2 13 3 26.5t1 27.5q0 150-105 255T480-120Zm0-80q88 0 158-48.5T740-375q-20 5-40 8t-40 3q-123 0-209.5-86.5T364-660q0-20 3-40t8-40q-78 32-126.5 102T200-480q0 116 82 198t198 82Zm-10-270Z" />
              </S.DarkModeIcon>
            </S.ThemeToggleButton>{' '}
            <div>
              <S.SignInButton onClick={handleSignIn}>Sign In</S.SignInButton>
            </div>
          </S.NavContent>
        </S.NavContent>
      </S.Navbar>

      {/* Hero Section */}
      <S.HeroSection>
        <S.HeroContent>
          <S.LogoContainer>
            <S.LogoWrapper>
              {isDark ? (
                <>
                  <S.HeroLogo src={assessDark} alt="logo" />
                </>
              ) : (
                <>
                  <S.HeroLogo src={assessLight} alt="logo" />
                </>
              )}
            </S.LogoWrapper>
          </S.LogoContainer>
          <S.HeroTitle>AI Assessment Tool, for Better Outcomes</S.HeroTitle>
          <S.HeroSubtitle>
            Get instant, AI-powered guidance tailored to your unique requirements.
          </S.HeroSubtitle>
          <div>
            <S.CTAButton href="#demo" onClick={handleScrollToDemo}>
              Get A Demo Now!
            </S.CTAButton>
          </div>
        </S.HeroContent>
      </S.HeroSection>

      {/* Content Sections */}
      <S.ContentSection>
        <div>
          {/* Section 1 */}
          <S.Section>
            <S.SectionHeader>What is an AI Assessment Tool?</S.SectionHeader>
            <S.SectionText>
              The AI Assessment Tool is an intelligent solution designed to streamline the
              preparation of Assessment Reports and Decision Notices for development applications
              submitted to local councils.
            </S.SectionText>
            <S.SectionText>
              The platform begins by collecting the standard documentation required for a specific
              development proposal. It then automatically analyses and processes the submitted
              information against the relevant council planning scheme, policies, and regulatory
              requirements. Based on this assessment, the system generates draft Assessment Reports
              and Decision Notices that align with applicable planning provisions.
            </S.SectionText>
            <S.SectionText>
              While the tool significantly automates the assessment and report generation process,
              all generated outputs remain subject to review and verification by qualified planning
              professionals. Planners retain full control over the final determination and can amend
              the generated documents to ensure they accurately reflect their professional judgement
              and the specific circumstances of the application.
            </S.SectionText>
            <S.SectionText>
              By reducing the time spent on manual analysis and document preparation, the AI
              Assessment Tool improves efficiency, enhances consistency, and allows planning
              professionals to focus on higher-value decision-making activities.
            </S.SectionText>
          </S.Section>

          {/* Section 2 */}
          <S.Section>
            <S.SectionHeader>Use Case:</S.SectionHeader>
            <S.SectionText>
              The assessment and report generation process within local councils is traditionally a
              manual, time-intensive activity that requires significant staff resources. Planning
              officers are often required to review large volumes of documentation, assess proposals
              against planning scheme requirements, and prepare detailed Assessment Reports and
              Decision Notices. This process can be repetitive, resource-intensive, and costly,
              particularly when managing high application volumes.
            </S.SectionText>

            <S.SectionText>
              To address these challenges, Urban Compass has developed the AI Assessment Tool, an
              intelligent solution that automates the assessment and report generation workflow. By
              analysing submitted development documentation against relevant planning schemes,
              policies, and regulatory requirements, the platform can generate draft Assessment
              Reports and Decision Notices in a fraction of the time required through traditional
              methods.
            </S.SectionText>
            <S.SectionText>
              The solution significantly reduces administrative workload, improves consistency
              across assessments, and enables planning professionals to focus on higher-value review
              and decision-making activities while maintaining full oversight of the final outcome.
            </S.SectionText>
          </S.Section>

          {/* Section 3 */}
          <S.Section>
            <S.SectionHeader>How do you get your own AI Assessment Tool?</S.SectionHeader>
            <S.SectionText>
              Obtaining a customised AI Document Checker is a straightforward process supported by
              our team from initial consultation through to deployment.
            </S.SectionText>
            <S.SectionText>
              The journey begins with a discovery session where we work closely with your
              organisation to understand your requirements, document types, business processes, and
              consistency-checking objectives. During this phase, you provide the documents,
              datasets, and domain knowledge that will be used to train and configure the solution.
            </S.SectionText>
            <S.SectionText>
              Once the requirements have been established, our team develops and tunes the AI
              Assessment Tool to recognize, extract, and analyze the documents relevant to your
              organisation. The system is tailored to your unique workflows, ensuring accurate and
              meaningful consistency assessments across your documents.
            </S.SectionText>
            <S.SectionText>
              Following successful testing and validation, a dedicated AI Assessment instance is
              deployed for your organisation. This secure environment can be accessed through our
              application using the credentials provided, enabling authorised users to upload
              documents and review reports with ease.
            </S.SectionText>
            <S.SectionText>
              From onboarding and configuration to deployment and ongoing support, we provide an
              end-to-end service designed to ensure a seamless, secure, and efficient implementation
              that aligns with your business objectives and operational requirements.
            </S.SectionText>
          </S.Section>
        </div>
      </S.ContentSection>

      {/* Contact Section */}
      <S.ContactSection id="demo">
        <S.ContactGrid>
          <S.ContactInfo>
            <S.ContactTitle>Get in touch</S.ContactTitle>
            <S.ContactDescription>
              Have questions or need help with your project? Our team is here to support you every
              step of the way. Reach out to us and we'll get back to you as soon as possible.
            </S.ContactDescription>
            <S.ContactDetails>
              <S.ContactItem>
                <S.ContactLabel>Address:</S.ContactLabel>
                <S.ContactValue>
                  Melbourne <br />
                  315/566 St Kilda Rd Melbourne Victoria 3000
                </S.ContactValue>
              </S.ContactItem>
              <S.ContactItem>
                <S.ContactLabel>Email:</S.ContactLabel>
                <S.ContactValue>info@urbancompasssoftware.com</S.ContactValue>
              </S.ContactItem>
              <S.ContactItem>
                <S.ContactLabel>Contact:</S.ContactLabel>
                <S.ContactValue>+61 1300 100 436</S.ContactValue>
              </S.ContactItem>
            </S.ContactDetails>
          </S.ContactInfo>

          <div>
            <S.FormCard>
              <S.FormGrid>
                <S.FormGroup>
                  <S.FormLabel>
                    Enter First Name:<S.Required>*</S.Required>
                  </S.FormLabel>
                  <S.FormInput
                    type="text"
                    name="firstName"
                    placeholder="Enter Your First Name"
                    value={formData.firstName}
                    onChange={handleInputChange}
                  />
                </S.FormGroup>
                <S.FormGroup>
                  <S.FormLabel>
                    Enter Last Name:<S.Required>*</S.Required>
                  </S.FormLabel>
                  <S.FormInput
                    type="text"
                    name="lastName"
                    placeholder="Enter Your Last Name"
                    value={formData.lastName}
                    onChange={handleInputChange}
                  />
                </S.FormGroup>
                <S.FormGroup>
                  <S.FormLabel>
                    Enter Email:<S.Required>*</S.Required>
                  </S.FormLabel>
                  <S.FormInput
                    type="email"
                    name="email"
                    placeholder="Enter Your Email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </S.FormGroup>
                <S.FormGroup>
                  <S.FormLabel>
                    Enter Contact Number:<S.Required>*</S.Required>
                  </S.FormLabel>
                  <S.FormInput
                    type="text"
                    name="phoneNumber"
                    placeholder="Enter Your Contact Number"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                  />
                </S.FormGroup>
              </S.FormGrid>
              <S.FormGroup style={{ marginTop: '1.25rem' }}>
                <S.FormLabel>
                  Enter Message:<S.Required>*</S.Required>
                </S.FormLabel>
                <S.FormTextarea
                  name="message"
                  placeholder="Enter your message"
                  value={formData.message}
                  onChange={handleInputChange}
                />
              </S.FormGroup>
              <S.FormActions>
                <S.SubmitButton onClick={handleSubmit} disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </S.SubmitButton>
              </S.FormActions>
            </S.FormCard>
          </div>
        </S.ContactGrid>
      </S.ContactSection>

      {/* Acknowledgement */}
      <S.AcknowledgementSection>
        <S.AcknowledgementText>
          In the spirit of reconciliation, The Urban Compass Software Pty Ltd acknowledges the
          Traditional Custodians of country throughout Australia and their connections to land, sea
          and community. We pay our respect to their Elders past and present and extend that respect
          to all First Nations peoples today with disability, their families, and their carers.
        </S.AcknowledgementText>
        <S.AcknowledgementImage>
          <img src={traditionalFlag} alt="traditionalFlag" />
        </S.AcknowledgementImage>
      </S.AcknowledgementSection>

      <S.Footer>
        <S.FooterWrapper>
          <S.FooterLeftSection>
            <S.FooterText>
              © {getCurrentYear()}{' '}
              <S.FooterLink href="https://urbancompasssoftware.com/" target="_blank">
                Urban Compass
              </S.FooterLink>
              . All rights reserved.
            </S.FooterText>
            <S.FooterPolicies>
              <Link to="/terms">Terms and Conditions</Link> |{' '}
              <Link to="/policy">Privacy Policy</Link>
            </S.FooterPolicies>
          </S.FooterLeftSection>

          <S.FooterCenterSection>
            <S.SocialLinks>
              <S.SocialIcon
                href="https://www.linkedin.com/company/urban-compass-software/?viewAsMember=true"
                target="_blank"
                aria-label="LinkedIn"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                  <rect width="4" height="12" x="2" y="9" />
                  <circle cx="4" cy="4" r="2" />
                </svg>
              </S.SocialIcon>
              <S.SocialIcon
                href="https://www.youtube.com/@UrbanCompassSoftware"
                target="_blank"
                aria-label="YouTube"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
                  <path d="m10 15 5-3-5-3z" />
                </svg>
              </S.SocialIcon>
              <S.SocialIcon href="#" aria-label="Instagram">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
                </svg>
              </S.SocialIcon>
            </S.SocialLinks>
          </S.FooterCenterSection>

          {/* <S.FooterRightSection>
            <S.AppStoresContainer>
              <AppleStore
                onClick={() =>
                  window.open(
                    'https://apps.apple.com/in/app/urban-compass-chatbot/id6759237257',
                    '_blank'
                  )
                }
                style={{ cursor: 'pointer' }}
              />
              <GoogleStore
                onClick={() =>
                  window.open(
                    'https://play.google.com/store/apps/details?id=com.coderunner.urban_chatbot',
                    '_blank'
                  )
                }
                style={{ cursor: 'pointer' }}
              />
            </S.AppStoresContainer>
          </S.FooterRightSection> */}
        </S.FooterWrapper>
      </S.Footer>
    </S.PageWrapper>
  );
};

export default LandingPage;
